import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import React, { useEffect, useMemo, useRef } from 'react';
import * as uuid from 'uuid';
import { useAppConfig } from '@/config/context';
import { getParamFromUrl } from '@/infrastructure/history';
import { isAndroid, isApple } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  MatchMode,
  NUMBER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { FilterEntry } from '@/presentation/components/form/types';
import { iconText } from '@/presentation/components/hint-text/hintText';
import {
  enumFilterTips,
  numberFilterTips,
  versionFilterTips,
} from '@/presentation/components/issue-list/components/tips';
import { ITdsFormRef, TdsFilterType, TdsForm, TdsFormItem, TdsFormItemType } from '@/presentation/components/tds-form';
import { DEFAULT_ISSUE_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { TdsCustomFieldsValue } from '@/presentation/components/tds-form/tds-custom-fields';
import { getInitTimeRange } from '@/presentation/components/tds-form/tds-date-cascader';
import { COMMON_TIME_TYPE_OPTIONS, ISSUE_TIME_TYPE } from '@/presentation/components/tds-form/tds-date-cascader/config';
import { TdsColumnGroup } from '@/presentation/components/tds-form/type';
import {
  customFields2Filter,
  customFilter,
  dateCascaderConverter,
  dateCascaderTransformer,
  simpleField2Filter,
} from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import {
  ANALYZE,
  HPROF_FILE_STATUS_OPTIONS,
  HprofFileStatusType,
  INTENSIVE,
  MemoryDetailFieldKey,
  SINGLE,
  StandardMemoryDetailFieldKey,
} from '@/presentation/pages/memory/memory-detail/config';
import { CommonFieldKey, StandardCommonFieldKey, Zone } from '@/proto/field';
import { IssueType } from '@/proto/issue';
import { IFilter } from '@/proto/metric';
import { genStandardToken, getTagParams, KeyDataType, parseStandardToken } from '@/proto/token';


export const MemoryDetailProblemForm = (props: {
  onTokenChange?: (token: string) => void;
  name?: string;
  entry?: FilterEntry;
  tabKey: string;
  formRef: React.MutableRefObject<ProFormInstance<any> | undefined>;
  tableFormRef?: React.MutableRefObject<ProFormInstance<any> | undefined>;
  tdsFormRef?: React.RefObject<ITdsFormRef>;
  subTdsFormRef?: React.RefObject<ITdsFormRef>;
}) => {
  const { tabKey, formRef: fatherFormRef, tableFormRef, tdsFormRef, subTdsFormRef, entry } = props;
  const ref = useRef<ProFormInstance>();
  const config = useAppConfig();
  const formRef = fatherFormRef || ref;
  const [state, setState] = useUrlState({
    token: getParamFromUrl('token') || '',
  });
  const [timeRange, setTimeRange] = useLocalStorageState(
    `memory-detail-timeRange-${name}-${tabKey}`,
    {
      defaultValue: DEFAULT_ISSUE_TIME_RANGE,
    },
  );
  const [, useTdsFormContext] = useTdsForm;

  const { updateFormContext } = useTdsFormContext();

  const items: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: '时间范围',
      name: StandardCommonFieldKey.TimeRange,
      width: 462,
      deleteable: false,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      fieldProps: {
        maxLimitedDays: 90,
        timeWindowOptions: ISSUE_TIME_TYPE,
        timeTypeOptions: COMMON_TIME_TYPE_OPTIONS,
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.CharacterInput,
      labelTools: [iconText(versionFilterTips)],
      label: 'APP版本',
      labelString: 'APP版本',
      name: StandardCommonFieldKey.AppVersion,
      deleteable: false,
      width: 360,
      inFilter: true,
      filterType: TdsFilterType.Version,
      filterName: CommonFieldKey.AppVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AppVersion,
          fieldScope: Zone.MemDetail,
        },
        modeSelectProps: {
          style: {
            width: 106,
          },
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.FieldSelector,
      name: StandardCommonFieldKey.AppVersionMode,
      group: TdsColumnGroup.ApplicationInfo,
      inFilter: true,
      labelTools: [iconText(enumFilterTips)],
      labelString: '构建类型',
      label: '构建类型',
      fieldProps: {
        mode: 'multiple',
        fieldKey: CommonFieldKey.AppVersionMode,
        fieldScope: Zone.MemDetail,
      },
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFilter(CommonFieldKey.AppVersionMode, { values: fieldValue }),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '用户ID',
      name: StandardCommonFieldKey.AccountId,
      group: TdsColumnGroup.UserInfo,
      filterName: CommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '设备ID',
      name: StandardCommonFieldKey.Qimei,
      group: TdsColumnGroup.DeviceInfo,
      filterName: CommonFieldKey.Qimei,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Bundle ID',
      name: StandardCommonFieldKey.BundleId,
      group: TdsColumnGroup.ApplicationInfo,
      filterName: CommonFieldKey.BundleId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.BundleId,
          fieldScope: Zone.MemDetail,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: isApple(),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '场景',
      name: StandardCommonFieldKey.Stage,
      group: TdsColumnGroup.ContextInfo,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Stage,
          fieldScope: Zone.MemDetail,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.Stage,
      filterType: TdsFilterType.String,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '厂商',
      name: StandardCommonFieldKey.Brand,
      group: TdsColumnGroup.DeviceInfo,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Brand,
          fieldScope: Zone.MemDetail,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.Brand,
      filterType: TdsFilterType.String,
      hidden: isApple(),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '实验ID',
      hidden: !config.enableShiplyAbTestTag,
      group: TdsColumnGroup.Custom,
      name: StandardCommonFieldKey.ShiplyTag,
      filterName: CommonFieldKey.ShiplyTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a11'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.ShiplyTag,
          fieldScope: Zone.MemDetail,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '业务下钻',
      name: StandardCommonFieldKey.AbTestTag,
      group: TdsColumnGroup.Custom,
      filterName: CommonFieldKey.ShiplyAbTestTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a12'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.AbTestTag,
          fieldScope: Zone.MemDetail,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '个例标签',
      name: StandardCommonFieldKey.TagsInFilter,
      group: TdsColumnGroup.Tag,
      filterName: CommonFieldKey.TagsInFilter,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.TagsInFilter, fieldValue, 'a13'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.TagsInFilter,
          fieldScope: Zone.MemDetail,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'SDK版本',
      name: StandardCommonFieldKey.SdkVersion,
      group: TdsColumnGroup.ApplicationInfo,
      filterName: CommonFieldKey.SdkVersion,
      inFilter: true,
      filterType: TdsFilterType.Version,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.SdkVersion,
          fieldScope: Zone.MemDetail,
        },
        mode: 'tags',
        modeOptions: VERSION_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '机型',
      name: StandardCommonFieldKey.Model,
      group: TdsColumnGroup.DeviceInfo,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Model,
          fieldScope: Zone.MemDetail,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '系统版本',
      name: StandardCommonFieldKey.OsVersion,
      group: TdsColumnGroup.DeviceInfo,
      filterType: TdsFilterType.Version,
      filterName: CommonFieldKey.OsVersion,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.OsVersion,
          fieldScope: Zone.MemDetail,
        },
        mode: 'tags',
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      inFilter: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '进程',
      name: StandardCommonFieldKey.ProcessName,
      group: TdsColumnGroup.ContextInfo,
      filterName: CommonFieldKey.ProcessName,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: isApple(),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '启动ID',
      name: StandardCommonFieldKey.LaunchId,
      group: TdsColumnGroup.ContextInfo,
      filterName: CommonFieldKey.LaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '消息ID',
      name: StandardCommonFieldKey.ClientIdentify,
      group: TdsColumnGroup.exceptionInfo,
      filterName: CommonFieldKey.ClientIdentify,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Issue ID',
      name: StandardCommonFieldKey.Feature,
      group: TdsColumnGroup.exceptionInfo,
      filterName: CommonFieldKey.Feature,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: tabKey === ANALYZE,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '进程启动ID',
      name: StandardCommonFieldKey.ProcessLaunchId,
      group: TdsColumnGroup.exceptionInfo,
      filterName: CommonFieldKey.ProcessLaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: isApple(),
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: '虚拟机最大堆内存(MB)',
      name: StandardCommonFieldKey.VmMaxSize,
      group: TdsColumnGroup.ContextInfo,
      filterName: CommonFieldKey.VmMaxSize,
      inFilter: true,
      fieldProps: {
        isDefaultQueryOptions: true,
        fieldKey: CommonFieldKey.VmMaxSize,
        fieldScope: Zone.MemDetail,
        optionTransform: option => option?.map(item => ({
          ...item,
          label: !isNaN(Number(item.label))
            ? Math.round(Number(item.label) / 1024 / 1024)
            : item.label,
        })),
      },
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFilter(CommonFieldKey.VmMaxSize, { values: fieldValue }),
      hidden: isApple(),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '问题特征',
      name: StandardCommonFieldKey.FuncName,
      group: TdsColumnGroup.exceptionInfo,
      filterName: CommonFieldKey.FuncName,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: tabKey === ANALYZE,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'ROM',
      name: StandardCommonFieldKey.Rom,
      group: TdsColumnGroup.DeviceInfo,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Rom,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Rom,
          fieldScope: Zone.MemDetail,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: !isAndroid(),
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: '日志来源',
      name: StandardCommonFieldKey.DumpReason,
      group: TdsColumnGroup.exceptionInfo,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.DumpReason,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.DumpReason,
          fieldScope: Zone.MemDetail,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      hidden: !isAndroid(),
    },
    {
      type: TdsFormItemType.NumberInput,
      labelTools: [iconText(numberFilterTips)],
      label: '使用时长（秒）',
      labelString: '使用时长（秒）',
      name: StandardCommonFieldKey.StandardLaunchDuration,
      group: TdsColumnGroup.ContextInfo,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.LaunchDuration,
      filterType: TdsFilterType.Number,
      hidden: tabKey !== ANALYZE,
    },
    {
      type: TdsFormItemType.CustomRange,
      label: '对象大小范围(MB)',
      group: TdsColumnGroup.exceptionInfo,
      inFilter: true,
      filterName: MemoryDetailFieldKey.DeatilObjectSize,
      name: StandardMemoryDetailFieldKey.DeatilObjectSize,
      hidden: !(
        entry
        && [
          FilterEntry.MemoryDetailDetail,
          FilterEntry.MemoryDetailAnalysis,
        ].includes(entry)
      ),
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
        modeSelectProps: {
          disabled: true,
          showArrow: false,
        },
      },
      initialValue: {
        mode: MatchMode.Between,
        values: undefined,
      },
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => {
        const list = [];
        const min = fieldValue.values?.min;
        const max = fieldValue.values?.max;
        if (min) {
          list.push({
            name: CommonFieldKey.ObjectSize,
            values: [!isNaN(min) ? String(Number(min) * 1024 * 1024) : min],
            operator: 'GE',
          });
        }
        if (max) {
          list.push({
            name: CommonFieldKey.ObjectSize,
            values: [!isNaN(max) ? String(Number(max) * 1024 * 1024) : max],
            operator: 'LE',
          });
        }
        return list;
      },
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: '只显示泄漏个例',
      name: StandardCommonFieldKey.LeakObjectType,
      group: TdsColumnGroup.Tag,
      fieldProps: {
        options: [
          { value: 'leak', label: '是' },
          { value: '0', label: '否' },
        ],
      },
      inFilter: true,
      filterName: CommonFieldKey.LeakObjectType,
      filterType: TdsFilterType.Custom,
      filterTransform: simpleField2Filter,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: '上报次数',
      name: StandardCommonFieldKey.DocCount,
      group: TdsColumnGroup.exceptionInfo,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.DocCount,
      filterType: TdsFilterType.Number,
      hidden:
        tabKey === ANALYZE
        || entry === FilterEntry.MemoryDetailDetail
        || entry === FilterEntry.MemoryDetailAnalysis,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: '设备数',
      name: StandardCommonFieldKey.QimeiCount,
      group: TdsColumnGroup.exceptionInfo,
      fieldProps: {
        modeOptions: NUMBER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.QimeiCount,
      filterType: TdsFilterType.Number,
      hidden:
        tabKey === ANALYZE
        || entry === FilterEntry.MemoryDetailDetail
        || entry === FilterEntry.MemoryDetailAnalysis,
    },

    {
      type: TdsFormItemType.Area,
      inFilter: true,
      name: StandardCommonFieldKey.Area,
      label: '地域',
      group: TdsColumnGroup.UserInfo,
      fieldProps: {
        fieldScope: Zone.MemDetail,
      },
      hideInCompare: true,
      filterType: TdsFilterType.Custom,
      filterName: StandardCommonFieldKey.Area,
      filterTransform: (_fieldKey: string, fieldValue: TdsCustomFieldsValue[]) => fieldValue?.map((item: any) => ({
        name: item.name,
        operator: item.operator,
        values: item.values || [],
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: '自定义维度',
      name: StandardCommonFieldKey.UserCustom,
      group: TdsColumnGroup.Custom,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.UserCustom,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_MEMORY_DETAIL,
        type: 'customFields',
      },
      filterTransform: (
        _fieldKey: string,
        fieldValue: TdsCustomFieldsValue[],
      ) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.UserCustom,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    {
      type: TdsFormItemType.CustomPanel,
      label: '自定义字段',
      name: StandardCommonFieldKey.BizExtendInfo,
      group: TdsColumnGroup.Custom,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterName: CommonFieldKey.BizExtendInfo,
      fieldProps: {
        issueType: IssueType.ISSUE_TYPE_MEMORY_DETAIL,
        type: 'customKey',
      },
      filterTransform: (
        _fieldKey: string,
        fieldValue: TdsCustomFieldsValue[],
      ) => fieldValue?.map((item: any) => ({
        name: CommonFieldKey.BizExtendInfo,
        operator: item.operator,
        values: item.values || [],
        key: item.key,
        type: KeyDataType.STRING,
      })) ?? [],
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: '堆转储文件分析状态',
      name: CommonFieldKey.HprofFileStatus,
      group: TdsColumnGroup.exceptionInfo,
      fieldProps: {
        options: HPROF_FILE_STATUS_OPTIONS,
      },
      hidden: tabKey !== ANALYZE,
      filterType: TdsFilterType.EnumString,
    },
  ];

  const initToken = async () => {
    if (!state.token) {
      formRef.current?.submit?.();
    } else {
      const fieldsValue = await parseStandardToken(state.token);
      // 链接有token时，强制展示有value值的表单项
      tdsFormRef?.current?.showFormItem?.(items, fieldsValue)?.then?.(() => {
        formRef?.current?.setFieldsValue?.(fieldsValue);
        tableFormRef?.current?.setFieldsValue(fieldsValue);
      });
      updateFormContext(draft => ({
        ...draft,
        fieldsValue,
        fieldsFilter: fieldsValue?.filters ?? [],
        uuidFetch: uuid.v4(),
      }));
      props.onTokenChange?.(state.token);
    }
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    const leakObjectTypeFilter = filters?.filter(filter => filter?.name === CommonFieldKey.LeakObjectType);
    if (
      leakObjectTypeFilter?.length > 0
      && leakObjectTypeFilter?.[0]?.values?.includes('0')
    ) {
      const index = filters?.findIndex(filter => filter?.name === CommonFieldKey.LeakObjectType);
      if (index !== -1) {
        filters?.splice(index, 1);
      }
    }

    let hprofFileStatus: HprofFileStatusType;
    if (tabKey === ANALYZE && values?.[CommonFieldKey.HprofFileStatus]) {
      hprofFileStatus = values?.[CommonFieldKey.HprofFileStatus];
    }
    // 缓存选择过的时间范围
    if ([SINGLE, INTENSIVE].includes(tabKey)) {
      filters.push({
        name: CommonFieldKey.ObjectType,
        values: tabKey === SINGLE ? ['single_large_object'] : ['dense_object'],
        operator: 'MATCH',
      });
    }
    // 需要带上表格下方的筛选项
    const tableValues = tableFormRef?.current?.getFieldsValue?.();
    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? DEFAULT_ISSUE_TIME_RANGE);
    const concatFilters = (filters || [])?.concat(subTdsFormRef?.current?.getFilter?.() || []);
    const token = await genStandardToken({
      ...values,
      ...tableValues,
      issue_status: tableValues?.standard_issue_status?.select_value,
      tapd_status: tableValues?.standard_tapd_status?.select_value,
      tapd_handler: tableValues?.standard_tapd_handler?.select_value,
      issue_status_operator: tableValues?.standard_issue_status?.switch_value ? 'NOT' : '',
      tapd_status_operator: tableValues?.standard_tapd_status?.switch_value ? 'NOT' : '',
      tapd_handler_operator: tableValues?.standard_tapd_handler?.switch_value ? 'NOT' : '',
      new_tag_list_operator: tableValues?.standard_tags_list?.switch_value ? 'NOT' : '',
      new_tag_list: tableValues?.standard_tags_list?.select_value?.length
        ? await getTagParams(tableValues?.standard_tags_list?.select_value, undefined) : [],
      tags: tableValues?.[CommonFieldKey.tagList]?.length
        ? await getTagParams(tableValues[CommonFieldKey.tagList]!, undefined) : undefined,
      time_window: values.time_window_type,
      filters: concatFilters,
    });

    updateFormContext(draft => ({
      ...draft,
      token,
      fieldsValue: values,
      fieldsFilter: concatFilters,
      uuidFetch: uuid.v4(),
      hprofFileStatus,
    }));

    setState({
      token,
    });
    props.onTokenChange?.(token);
  };

  useEffect(() => {
    initToken();
  }, []);

  const initialTimeRange = useMemo(() => (timeRange ? {
    ...timeRange,
    date_value: getInitTimeRange(timeRange.time_window_type!, timeRange.short_cut!),
  } : timeRange), [timeRange]);

  return <TdsForm
    ref={tdsFormRef}
    pageId={PAGE_ID.memory.detail.detail}
    formRef={formRef}
    onFinish={onFinish}
    items={items}
    name={props.name || `memory-detail-list-${tabKey}`}
    initialValues={{
      [StandardCommonFieldKey.TimeRange]: initialTimeRange,
    }}
    renderByGroup
  />;
};
